import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Mail, Lock, User, Phone } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

import { AUTH_API } from '@/config/api';
import { getValidRedirectPath } from '@/config/authRedirect';
import { finishAuthRedirect } from '@/config/hosts';

const API = AUTH_API;

export default function RegisterForm({ onSuccess, onSwitchToLogin, role = 'patient' }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    agree_to_terms: false,
    agree_to_health_data: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateForm = () => {
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.agree_to_terms) {
      setError('You must agree to the terms and conditions');
      return false;
    }
    if (!formData.agree_to_health_data) {
      setError('You must consent to health data collection');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setLoading(true);

    try {
      const normalizedEmail = formData.email.toLowerCase().trim();
      const chosenRole = role === 'doctor' ? 'doctor' : 'patient';
      const response = await axios.post(
        `${API}/register`,
        {
          email: normalizedEmail,
          password: formData.password,
          full_name: formData.full_name?.trim() || '',
          phone: formData.phone.trim(),
          role: chosenRole,
          account_type: chosenRole,
          agree_to_terms: formData.agree_to_terms,
          agree_to_health_data: formData.agree_to_health_data,
        },
        {
          timeout: 60000,
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const { user } = response.data;
      if (!user) throw new Error('Invalid response from server');

      const userWithRole = { ...user, role: user.role || chosenRole };
      try {
        localStorage.setItem('byonco_user_role', chosenRole);
      } catch (_) {}

      await login(userWithRole);

      if (onSuccess) {
        onSuccess(userWithRole);
      } else {
        const redirectParam = searchParams.get('redirect');
        const defaultDest = chosenRole === 'doctor' ? '/oncoagent/app' : '/app';
        const redirectPath = getValidRedirectPath(redirectParam);
        finishAuthRedirect(navigate, redirectPath || defaultDest);
      }
    } catch (err) {
      let errorMessage = 'Registration failed. Please try again.';
      if (err.response?.status === 429) {
        errorMessage = 'Too many attempts. Please wait and try again.';
      } else if (typeof err.response?.data?.detail === 'string') {
        errorMessage = err.response.data.detail;
      } else if (!err.response) {
        errorMessage = 'Cannot reach the server. Please try again in a moment.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full rounded-[18px] bg-[#eef3ea] px-6 py-7 sm:px-7 sm:py-8 text-gray-900">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-forest-ink mb-1" style={{ fontFamily: "'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif" }}>
          {role === 'doctor' ? 'Create clinician account' : 'Create your account'}
        </h2>
        <p className="text-sm text-[#3d4a42]">
          {role === 'doctor'
            ? 'Join the OncoAgent oncology network for rapid referrals & patient handoffs'
            : 'Join ByOnco to keep your care details in one place'}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="text-sm bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        
        <div className="space-y-2">
          <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
            Full Name
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <User className="h-5 w-5" />
            </span>
            <input
              id="full_name"
              type="text"
              placeholder="Emily Carter (optional)"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email *
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Mail className="h-5 w-5" />
            </span>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Phone (optional)
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Phone className="h-5 w-5" />
            </span>
            <input
              id="phone"
              type="tel"
              placeholder="Optional"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password *
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Lock className="h-5 w-5" />
            </span>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
              required
              minLength={8}
            />
          </div>
          <p className="text-xs text-gray-500">Must be at least 8 characters</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
            Confirm Password *
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-charcoal">
              <Lock className="h-5 w-5" />
            </span>
            <input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full rounded-[14px] border-0 bg-white px-3 py-2.5 pl-10 text-sm text-gray-900 placeholder:text-charcoal focus:outline-none focus:ring-2 focus:ring-forest-ink focus:border-forest-ink transition"
              required
            />
          </div>
        </div>

        <div className="flex items-start space-x-3 pt-2">
          <Checkbox
            id="terms"
            checked={formData.agree_to_terms}
            onCheckedChange={(checked) => setFormData({ ...formData, agree_to_terms: checked })}
            required
            className="mt-1"
          />
          <label htmlFor="terms" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
            I agree to the{' '}
            <a href="/terms-and-conditions" target="_blank" className="text-forest-ink hover:text-blue-700 underline underline-offset-2 transition">
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" className="text-forest-ink hover:text-blue-700 underline underline-offset-2 transition">
              Privacy Policy
            </a>
          </label>
        </div>

        <div className="flex items-start space-x-3 pt-2">
          <Checkbox
            id="health-data"
            checked={formData.agree_to_health_data}
            onCheckedChange={(checked) => setFormData({ ...formData, agree_to_health_data: checked })}
            required
            className="mt-1"
          />
          <label htmlFor="health-data" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
            I consent to ByOnco collecting and processing my health information as described in the{' '}
            <a href="/health-privacy" target="_blank" className="text-forest-ink hover:text-blue-700 underline underline-offset-2 transition">
              Consumer Health Data Privacy Policy
            </a>
          </label>
        </div>

        <button
          type="submit"
          className="w-full inline-flex items-center justify-center rounded-full px-4 py-3 text-sm font-semibold bg-forest-ink text-cream-paper hover:bg-forest-shadow disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account'
          )}
        </button>

        <div className="text-center pt-2">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-sm font-medium text-forest-ink hover:text-blue-700 underline underline-offset-2 transition"
            >
              Sign in
            </button>
          </p>
        </div>
      </form>
    </div>
  );
}




